import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '../TextField/TextField';
import CircularProgress from '../Progress/EvoraSpinner';

/**
 * AsyncAutoCompleteField - Autocomplete component designed for async/search-as-you-type use cases
 *
 * Props:
 * - value: Selected option object (controlled)
 * - inputValue: Text in the input field (controlled)
 * - options: Array of options to display
 * - loading: Boolean to show loading indicator
 * - onChange: Callback when selection changes (event, value)
 * - onInputChange: Callback when input text changes (event, value, reason)
 * - label: Field label
 * - placeholder: Input placeholder text
 * - helperText: Helper text below field
 * - noOptionsText: Text when no options available
 * - renderOption: Custom render function for options
 * - disabled: Disable the field
 * - InputProps: Additional props for the input (e.g., startAdornment)
 * - getOptionLabel: Function to get label from option (default: option.label)
 */
export default function AsyncAutoCompleteField({
    value = null,
    inputValue = '',
    options = [],
    loading = false,
    onChange,
    onInputChange,
    label,
    placeholder,
    helperText,
    noOptionsText = 'No options',
    renderOption,
    disabled = false,
    InputProps = {},
    getOptionLabel,
    isOptionEqualToValue,
    error,
    required,
    fullWidth = true,
    size = 'small'
}) {
    return (
        <Autocomplete
            value={value}
            inputValue={inputValue}
            options={options}
            loading={loading}
            onChange={onChange}
            onInputChange={onInputChange}
            filterOptions={(x) => x} // Disable client-side filtering for server-side search
            getOptionLabel={getOptionLabel || ((option) => option?.label || '')}
            isOptionEqualToValue={isOptionEqualToValue || ((option, val) => option?.value === val?.value)}
            disabled={disabled}
            fullWidth={fullWidth}
            noOptionsText={noOptionsText}
            renderOption={renderOption}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    helperText={helperText}
                    error={error}
                    required={required}
                    size={size}
                    InputProps={{
                        ...params.InputProps,
                        ...InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
